/**
 * Course Builder — Admin landing page.
 *
 * Lists every course with status badges (Draft/Published/Archived), lesson +
 * exam counts, and quick actions (Edit, Duplicate, Publish, Unpublish,
 * Archive, Auto-Generate New).  Clicking a row navigates to
 * /admin/course-builder/:courseId for full editing.
 *
 * Route: /admin/course-builder
 * Auth: main_hub | department_admin | super_admin | training_director
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../config/canonical';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  BookOpen, Plus, Search, Sparkles, Loader2, Copy, Archive as ArchiveIcon,
  CheckCircle2, XCircle, Edit3, Eye, RefreshCw,
} from 'lucide-react';

const TOKEN_KEY = 'nexus_token';
const api = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const STATUS_STYLE = {
  PUBLISHED: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  DRAFT: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  ARCHIVED: 'bg-slate-500/15 text-slate-400 border-slate-500/40',
};

export default function CourseBuilderList() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [busy, setBusy] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('/api/training-center/admin/course-builder/courses');
      setCourses(r.courses || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const doAction = async (id, action, method = 'POST') => {
    setBusy(id + action);
    try {
      await api(`/api/training-center/admin/course-builder/courses/${id}/${action}`, { method });
      toast.success(`Course ${action}d`);
      refresh();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const duplicate = async (id) => {
    setBusy(id + 'dup');
    try {
      const r = await api(`/api/training-center/admin/course-builder/courses/${id}/duplicate`, { method: 'POST' });
      toast.success(`Duplicated as ${r.course.id}`);
      navigate(`/admin/course-builder/${r.course.id}`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const filtered = courses.filter(c => {
    if (statusFilter !== 'ALL' && (c.status || 'PUBLISHED') !== statusFilter) return false;
    const needle = q.toLowerCase().trim();
    if (!needle) return true;
    return `${c.id} ${c.title} ${c.code} ${c.category}`.toLowerCase().includes(needle);
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6 md:p-8" data-testid="course-builder-list">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-amber-400" /> Course Builder
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Create, edit, publish, and version every training course. Draft courses are invisible to investigators.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setAiOpen(true)}
              className="bg-purple-500 hover:bg-purple-400 text-white"
              data-testid="cb-ai-gen-btn">
              <Sparkles className="w-4 h-4 mr-1" /> Auto-Generate Course
            </Button>
            <Button onClick={() => navigate('/admin/course-builder/new')}
              className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
              data-testid="cb-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New Course
            </Button>
            <Button variant="outline" onClick={refresh} data-testid="cb-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search by title, code, category…"
                className="pl-9 bg-slate-950 border-slate-800 text-slate-100"
                data-testid="cb-search" />
            </div>
            {['ALL', 'DRAFT', 'PUBLISHED', 'ARCHIVED'].map(s => (
              <Button key={s} size="sm"
                variant={statusFilter === s ? 'default' : 'outline'}
                onClick={() => setStatusFilter(s)}
                className={statusFilter === s ? 'bg-amber-500 !text-slate-900' : ''}
                data-testid={`cb-filter-${s.toLowerCase()}`}>
                {s === 'ALL' ? `All (${courses.length})` : s}
              </Button>
            ))}
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading courses…
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-8 text-center text-slate-400">
              No courses match your filter. Click &quot;New Course&quot; or &quot;Auto-Generate Course&quot; to create one.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(c => {
              const status = c.status || (c.published ? 'PUBLISHED' : 'DRAFT');
              return (
                <Card key={c.id} className="bg-slate-900 border-slate-800 hover:border-amber-500/40 transition-colors"
                      data-testid={`cb-course-row-${c.id}`}>
                  <CardContent className="p-4 flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[260px] cursor-pointer" onClick={() => navigate(`/admin/course-builder/${c.id}`)}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-mono text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">{c.id}</span>
                        <h3 className="font-semibold text-slate-100">{c.title}</h3>
                        <Badge className={`text-xs border ${STATUS_STYLE[status] || STATUS_STYLE.DRAFT}`}>{status}</Badge>
                        {c.core && <Badge className="text-xs bg-blue-500/15 text-blue-300 border border-blue-500/40">CORE</Badge>}
                        {c.locked && <Badge className="text-xs bg-red-500/15 text-red-300 border border-red-500/40">LOCKED</Badge>}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {c.category} · {c.lessons_count} lesson{c.lessons_count !== 1 ? 's' : ''} ·
                        {' '}{c.exam_pool_size} exam Q · Pass ≥ {c.passing_score}% · v{c.version}
                        {c.last_edited_by_name ? ` · last edit by ${c.last_edited_by_name}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Button size="sm" variant="outline"
                        onClick={() => navigate(`/admin/course-builder/${c.id}`)}
                        data-testid={`cb-edit-${c.id}`}>
                        <Edit3 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => navigate(`/training-center/course/${c.id}`)}
                        data-testid={`cb-view-${c.id}`}>
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button size="sm" variant="outline"
                        disabled={busy === c.id + 'dup'}
                        onClick={() => duplicate(c.id)}
                        data-testid={`cb-dup-${c.id}`}>
                        <Copy className="w-4 h-4 mr-1" /> Duplicate
                      </Button>
                      {status === 'PUBLISHED' ? (
                        <Button size="sm" variant="outline"
                          disabled={busy === c.id + 'unpublish'}
                          onClick={() => doAction(c.id, 'unpublish')}
                          data-testid={`cb-unpublish-${c.id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Unpublish
                        </Button>
                      ) : (
                        <Button size="sm"
                          className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold"
                          disabled={busy === c.id + 'publish' || c.lessons_count === 0}
                          onClick={() => doAction(c.id, 'publish')}
                          data-testid={`cb-publish-${c.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Publish
                        </Button>
                      )}
                      {status !== 'ARCHIVED' && (
                        <Button size="sm" variant="outline"
                          disabled={busy === c.id + 'archive'}
                          onClick={() => { if (window.confirm(`Archive "${c.title}"? Investigators will lose access.`)) doAction(c.id, 'archive'); }}
                          data-testid={`cb-archive-${c.id}`}>
                          <ArchiveIcon className="w-4 h-4 mr-1" /> Archive
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {aiOpen && <AutoGenerateDialog onClose={() => setAiOpen(false)} onCreated={(c) => { setAiOpen(false); refresh(); navigate(`/admin/course-builder/${c.id}`); }} />}
    </div>
  );
}

function AutoGenerateDialog({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('FOUNDATIONS');
  const [lessons, setLessons] = useState(6);
  const [questions, setQuestions] = useState(20);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    setSubmitting(true);
    try {
      const r = await api('/api/training-center/admin/course-builder/auto-generate', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(), category,
          target_lesson_count: Math.max(1, Math.min(20, Number(lessons) || 6)),
          target_exam_questions: Math.max(5, Math.min(60, Number(questions) || 20)),
        }),
      });
      toast.success(`Course generated: ${r.lessons_generated} lessons, ${r.questions_generated} questions`);
      onCreated(r.course);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}
         data-testid="cb-ai-dialog">
      <div className="bg-slate-900 border border-slate-800 rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-purple-400" /> Auto-Generate Course
        </h2>
        <p className="text-sm text-slate-400 mb-4">
          GPT-5 will generate a full course outline with chapters, lesson bodies, knowledge checks, and an exam bank.
          Result is saved as a <b>DRAFT</b> — review and click Publish when ready.
        </p>
        <div className="space-y-3">
          <label className="block text-xs text-slate-400 mb-1">Course Title *</label>
          <Input value={title} onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Advanced Interview Techniques"
            className="bg-slate-950 border-slate-800 text-slate-100"
            data-testid="cb-ai-title" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Category</label>
              <Input value={category} onChange={e => setCategory(e.target.value.toUpperCase())}
                className="bg-slate-950 border-slate-800 text-slate-100"
                data-testid="cb-ai-category" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1"># Lessons</label>
              <Input type="number" min={1} max={20} value={lessons} onChange={e => setLessons(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-100"
                data-testid="cb-ai-lessons" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1"># Exam Qs</label>
              <Input type="number" min={5} max={60} value={questions} onChange={e => setQuestions(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-100"
                data-testid="cb-ai-questions" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !title.trim()}
            className="bg-purple-500 hover:bg-purple-400 text-white"
            data-testid="cb-ai-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-1" /> Generate</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
